// "Việc đã làm": nơi bot đã đăng bài / bình luận / tham gia — bấm vào địa chỉ để mở lại xem, lọc theo bot/kịch bản/ngày,
// tìm theo chữ trong địa chỉ hoặc tên nhóm, và cảnh báo nơi nào đã làm nhiều lần (khỏi làm trùng). Xuất Excel (CSV).
import { api, buildQuery, errMsg } from "../api";
import { busy, clear, gen, h, infoBox, toast } from "../dom";
import { fmtTime, laMucNoiBo } from "../format";
import { isSuper, loadBots, sortBots, store } from "../store";
import { cuoiNgayVN, dauNgayVN, kiemKhoangNgay } from "../../../../src/shared/khoang-ngay";
import { tenBuoc, tenKb } from "../ten-kb"; // L-78 / N9: TÊN kịch bản, TÊN bước thay mã
import { LOI_THIEU_CHU, chuanHoaGocNhin, loiDiaChiHttp, loiGocNhinBai, loiThieuKichBanBinhLuanBai, loiThieuKichBanThichTraLoi, type CauHinhHoiDongKiem } from "../../../../src/shared/hoi-dong-kiem"; // cùng phép kiểm với máy chủ

export type WorkKind = "dang" | "binh_luan" | "tra_loi" | "tham_gia" | "khac";
export interface Work {
  id: number; botId: string; botName: string; runKey: string; runId: string;
  bundleName: string; stepId: string; item: string | null; url: string; title: string | null; at: string; truocDo: number;
  kind: WorkKind; parentUrl: string | null; text: string | null; replyTo: string | null;
  ownerId?: string; // Boss sở hữu bot (máy chủ luôn trả; Super cần để đọc đúng cấu hình hội đồng của Boss đó)
}
export const KIND_TEXT: Record<WorkKind, string> = { dang: "đăng bài", binh_luan: "bình luận", tra_loi: "trả lời", tham_gia: "tham gia", khac: "khác" };
const KIND_TITLE: Record<WorkKind, string> = {
  dang: "Bot đã đăng một bài ở đây", binh_luan: "Bot đã bình luận ở đây", tra_loi: "Bot đã trả lời bình luận của người khác",
  tham_gia: "Bot đã tham gia nhóm/trang này", khac: "Việc khác bot làm xong ở đây",
};
interface Paged { items: Work[]; nextBefore: string | null }

/** N9: mục bot ghi bằng mã nội bộ ("i0") không hiện — chỉ tên nhóm/bài thật. */
export function noiLam(w: Pick<Work, "item">): string | null {
  return w.item && !laMucNoiBo(w.item) ? w.item : null;
}
/** L8: nút "Hội thoại" chỉ khi dòng có nội dung trao đổi (chữ bot gửi, bài gốc, hoặc là câu trả lời). */
export function coHoiThoai(w: Pick<Work, "text" | "parentUrl" | "kind" | "replyTo">): boolean {
  return !!(w.text || w.parentUrl || w.replyTo) || w.kind === "tra_loi";
}
/** L8: nút "Hội đồng" chỉ với dòng nằm trên một bài (bình luận / trả lời / bài đăng) — nơi các nick khác vào thích, trả lời hoặc
 *  cùng bình luận được. Dòng "tham gia" nhóm / "khác" không có bài để giao → không hiện nút. */
export function coHoiDong(w: Pick<Work, "kind">): boolean {
  return w.kind === "binh_luan" || w.kind === "tra_loi" || w.kind === "dang";
}

/** Rút gọn địa chỉ cho dễ đọc: bỏ https://www., cắt giữa nếu dài. */
export function shortUrl(u: string, max = 60): string {
  const s = u.replace(/^https?:\/\/(www\.)?/i, "");
  return s.length <= max ? s : `${s.slice(0, max - 12)}…${s.slice(-10)}`;
}

export async function renderWorks(root: HTMLElement, _p: Record<string, string>, query: URLSearchParams): Promise<() => void> {
  const alive = gen("works");
  const botSel = h("select", { title: "Chỉ hiện việc của một bot", "data-testid": "w-bot" }, h("option", { value: "" }, "Mọi bot"));
  const bundle = h("input", { placeholder: "tên kịch bản", title: "Chỉ hiện việc của một kịch bản (gõ đúng tên)", "data-testid": "w-bundle" });
  const q = h("input", { type: "search", placeholder: "tìm trong địa chỉ / tên nhóm / tiêu đề", maxlength: 200, title: "Chỉ hiện việc có chứa chữ này trong địa chỉ, tên nhóm hoặc tiêu đề trang", "data-testid": "w-q" });
  const from = h("input", { type: "date", title: "Chỉ hiện việc làm từ ngày này (giờ Việt Nam)", "data-testid": "w-from" });
  const to = h("input", { type: "date", title: "Chỉ hiện việc làm đến hết ngày này (giờ Việt Nam)", "data-testid": "w-to" });
  const onlyDup = h("input", { type: "checkbox", title: "Chỉ hiện nơi đã làm từ 2 lần trở lên — để rà xem có làm trùng không", "data-testid": "w-dup" });
  const kindSel = h("select", { title: "Chỉ hiện một loại việc: đăng bài, bình luận, trả lời (rep), tham gia", "data-testid": "w-kind" },
    h("option", { value: "" }, "Mọi loại"), ...(Object.keys(KIND_TEXT) as WorkKind[]).map((k) => h("option", { value: k }, KIND_TEXT[k])));
  kindSel.value = query.get("kind") ?? "";
  bundle.value = query.get("bundleName") ?? "";
  q.value = query.get("q") ?? "";
  from.value = query.get("from") ?? "";
  to.value = query.get("to") ?? "";
  const applyBtn = h("button", { type: "button", class: "pri", title: "Tải lại danh sách theo bộ lọc bên trên", "data-testid": "w-apply" }, "Lọc");
  const csv = h("a", { class: "btn", href: "#", title: "Tải danh sách việc đã làm dạng CSV (mở bằng Excel)", "data-testid": "w-csv" }, "Xuất Excel (CSV)");
  const info = h("p", { class: "muted small", "data-testid": "w-info" }, "Đang tải…");
  const tbody = h("tbody", { "data-testid": "w-rows" });
  const more = h("button", { type: "button", hidden: true, title: "Tải thêm 100 việc cũ hơn", "data-testid": "w-more" }, "Tải thêm");
  const errBox = h("div", { class: "err-box", hidden: true, "data-testid": "page-error" });
  const CHUA_CO_VIEC = "Chưa có việc nào được ghi. Bot ghi lại địa chỉ mỗi khi làm xong một bước rủi ro cao (đăng bài, bình luận) ở chế độ Chạy thật.";
  const empty = h("div", { class: "empty card", hidden: true, "data-testid": "w-empty" }, CHUA_CO_VIEC);
  const coLoc = (): boolean => Object.values(filters()).some((v) => v !== undefined);

  root.append(h("h1", {}, "Việc đã làm"),
    h("p", { class: "small muted" }, "Mỗi dòng là một nơi bot đã làm xong việc thật — bấm địa chỉ để mở lại xem. Cột ", h("b", {}, "Đã làm trước đó"), " cho biết địa chỉ đó từng được làm mấy lần rồi."),
    h("div", { class: "card" }, h("div", { class: "field-row" },
      h("label", { class: "f" }, h("span", {}, "Bot"), botSel),
      h("label", { class: "f" }, h("span", {}, "Loại việc"), kindSel),
      h("label", { class: "f" }, h("span", {}, "Kịch bản"), bundle),
      h("label", { class: "f" }, h("span", {}, "Từ ngày"), from),
      h("label", { class: "f" }, h("span", {}, "Đến ngày"), to)),
      h("label", { class: "f" }, h("span", {}, "Tìm chữ"), q),
      h("div", { class: "row" }, applyBtn, csv, h("label", { class: "inline", title: "Chỉ hiện nơi đã làm từ 2 lần trở lên" }, onlyDup, "Chỉ nơi làm trùng"))),
    info, errBox, empty,
    h("div", { class: "tbl" }, h("table", { "data-testid": "w-table" },
      h("thead", {}, h("tr", {},
        h("th", {}, "Lúc"), h("th", {}, "Bot"), h("th", { title: "Loại việc bot đã làm ở đây" }, "Loại"), h("th", { title: "Kịch bản và bước đã làm" }, "Kịch bản · bước"),
        h("th", { title: "Mục trong danh sách (tên nhóm, bài…) và tiêu đề trang" }, "Nơi làm"),
        h("th", { title: "Bấm để mở lại trang đã làm" }, "Địa chỉ"),
        h("th", { title: "Số lần địa chỉ này đã được làm trước đó — khác 0 là đang làm lại chỗ cũ" }, "Đã làm trước đó"),
        h("th", {}))),
      tbody)),
    h("p", {}, more));

  try {
    const bots = sortBots(await loadBots());
    if (!alive()) return () => {};
    for (const b of bots) botSel.append(h("option", { value: b.id }, isSuper() ? `${b.name} (${b.ownerName})` : b.name));
    botSel.value = query.get("botId") ?? "";
  } catch { /* không có danh sách bot thì vẫn lọc được bằng các ô khác */ }

  function filters(): Record<string, string | undefined> {
    return {
      botId: botSel.value || undefined,
      kind: kindSel.value || undefined,
      bundleName: bundle.value.trim() || undefined,
      q: q.value.trim() || undefined,
      from: dauNgayVN(from.value),
      to: cuoiNgayVN(to.value),
    };
  }
  let nextBefore: string | null = null;
  let count = 0;
  function row(w: Work): HTMLTableRowElement {
    const dup = w.truocDo > 0;
    return h("tr", { "data-testid": "w-row", dataset: { url: w.url, dup: String(dup) }, hidden: onlyDup.checked && !dup },
      h("td", { class: "nowrap small" }, fmtTime(w.at)),
      h("td", { class: "name" }, h("span", {}, w.botName)),
      h("td", { class: "nowrap" }, h("span", { class: `tag ${w.kind === "tra_loi" ? "running" : w.kind === "dang" ? "ok" : ""}`, title: KIND_TITLE[w.kind], "data-testid": "w-kind-tag" }, KIND_TEXT[w.kind])),
      h("td", { class: "small wrap" }, tenKb(w.bundleName, { lienKet: true }), h("div", { class: "muted", "data-testid": "w-buoc" }, tenBuoc(w.bundleName, w.stepId))),
      h("td", { class: "wrap small", "data-testid": "w-where" }, noiLam(w) ?? h("span", { class: "muted" }, "—"), w.title && w.title !== noiLam(w) ? h("div", { class: "muted" }, w.title) : null,
        w.text ? h("div", { class: "muted wrap", title: "Nội dung bot đã gửi" }, "“", w.text.slice(0, 120), w.text.length > 120 ? "…" : "", "”") : null),
      h("td", { class: "wrap" }, h("a", { href: w.url, target: "_blank", rel: "noopener noreferrer", title: `Mở lại: ${w.url}`, "data-testid": "w-link" }, shortUrl(w.url))),
      h("td", { class: "nowrap", "data-testid": "w-dup-count" }, dup ? h("span", { class: "tag warn", title: `Địa chỉ này đã được làm ${w.truocDo} lần trước đó — cân nhắc bỏ qua để khỏi trùng` }, `${w.truocDo} lần`) : h("span", { class: "muted" }, "lần đầu")),
      // L8: chỉ hiện nút khi dòng có dữ liệu cho nút đó (trước: mọi dòng đều 3 nút).
      h("td", { class: "nowrap" },
        coHoiThoai(w) ? [h("button", { type: "button", class: "mini", title: "Xem cả mạch trao đổi ở địa chỉ này: bot đăng/bình luận gì, ai trả lời, bot rep lại ra sao", "data-testid": "w-thread", onclick: (e: Event) => void openThread(w, e.currentTarget as HTMLButtonElement) }, "Hội thoại"), " "] : null,
        coHoiDong(w) ? [h("button", { type: "button", class: "mini", title: "Giao cho các nick khác vào đây: thích/trả lời đúng bình luận này, hoặc nhiều nick cùng bình luận vào bài", "data-testid": "w-hoidong", onclick: () => void openHoiDong(w) }, "Hội đồng"), " "] : null,
        h("a", { class: "btn mini", href: `#/runs/${encodeURIComponent(w.runKey)}`, title: "Mở lần chạy đã tạo ra việc này" }, "Lần chạy")));
  }
  async function loadPage(reset: boolean): Promise<void> {
    const g = gen("works-load");
    if (reset) { clear(tbody); nextBefore = null; count = 0; }
    errBox.hidden = true;
    const sai = kiemKhoangNgay(from.value, to.value); // L-79: báo ngay bằng câu tiếng Việt (trước đây không báo, hiện "Chưa có việc nào")
    if (sai) { clear(tbody); more.hidden = true; empty.hidden = true; info.textContent = ""; errBox.textContent = sai; errBox.hidden = false; return; }
    try {
      const p = await api<Paged>("GET", `/api/works${buildQuery({ ...filters(), limit: 100, before: nextBefore })}`);
      if (!g() || !alive()) return;
      for (const w of p.items) tbody.append(row(w));
      count += p.items.length;
      nextBefore = p.nextBefore;
      more.hidden = !nextBefore;
      empty.hidden = count > 0;
      // L-80: lọc ra 0 việc ≠ chưa có việc nào — nói đúng tình huống.
      if (!count) empty.textContent = coLoc() ? "Không có việc nào khớp bộ lọc — đổi hoặc xoá bớt điều kiện lọc rồi bấm Lọc." : CHUA_CO_VIEC;
      const dup = [...tbody.querySelectorAll('[data-testid="w-row"][data-dup="true"]')].length;
      // L8: "100 việc (còn nữa)" — số ĐÃ TẢI, nói rõ còn nữa như Lịch sử chạy (trước "100 việc" dễ hiểu nhầm là tổng).
      info.textContent = count ? `${count} việc${nextBefore ? " (còn nữa)" : ""}${dup ? ` · ${dup} nơi đã làm trùng` : ""}` : "";
      csv.href = `/api/works.csv${buildQuery(filters())}`;
    } catch (e) {
      if (!g() || !alive()) return;
      info.textContent = "";
      errBox.textContent = `Không tải được danh sách: ${errMsg(e)}`;
      errBox.hidden = false;
    }
  }
  async function openThread(w: Work, btn: HTMLButtonElement): Promise<void> {
    await busy(btn, "…", async () => {
      const goc = w.parentUrl ?? w.url;
      let items: Work[] = [];
      try { items = (await api<Paged>("GET", `/api/works${buildQuery({ parentUrl: goc, limit: 100 })}`)).items; } catch (e) { toast(errMsg(e), "err"); return; }
      const theoThoiGian = [...items].sort((a, b) => a.at.localeCompare(b.at));
      await infoBox(`Hội thoại tại ${shortUrl(goc, 46)}`, h("div", { "data-testid": "w-thread-box" },
        h("p", { class: "small" }, h("a", { href: goc, target: "_blank", rel: "noopener noreferrer", title: "Mở trang gốc" }, goc)),
        theoThoiGian.length
          ? h("div", { class: "thread" }, ...theoThoiGian.map((x) => h("div", { class: `thread-item ${x.kind}`, "data-testid": "w-thread-item" },
            h("div", { class: "small muted" }, fmtTime(x.at), " · ", x.botName, " · ", KIND_TEXT[x.kind]),
            x.replyTo ? h("div", { class: "thread-quote wrap", title: "Bình luận của người khác mà bot trả lời" }, "“", x.replyTo, "”") : null,
            h("div", { class: "wrap" }, x.text ?? h("span", { class: "muted" }, "(không ghi nội dung)")),
            h("div", { class: "small" }, h("a", { href: x.url, target: "_blank", rel: "noopener noreferrer", title: "Mở đúng chỗ này" }, shortUrl(x.url, 50))))))
          : h("p", { class: "muted" }, "Chưa có việc nào ở địa chỉ này.")));
    });
  }

  // Giao tay hội đồng nhiều nick từ đúng dòng việc đang xem.
  function openHoiDong(w: Work): Promise<void> {
    return new Promise<void>((resolve) => {
      const laBinhLuan = w.kind === "binh_luan";
      const chu = h("input", { value: (w.text ?? "").replace(/\s+/g, " ").trim().slice(0, 80), maxlength: 80, placeholder: "đoạn chữ riêng trong bình luận", title: "Đoạn chữ để nick khác tìm đúng bình luận này trên trang (không cần địa chỉ riêng của bình luận)", "data-testid": "wh-chu" });
      const goc = h("textarea", { rows: 4, placeholder: "người mới tìm hiểu\nđã dùng lâu\nhỏi giá", title: "Mỗi dòng là một góc nhìn; mỗi nick lấy một góc nhìn khác nhau để các câu không giống nhau", "data-testid": "wh-goc" });
      const err = h("p", { class: "err small", hidden: true, "data-testid": "wh-err" });
      const done = () => { wrap.remove(); resolve(); };
      const giaoBL = h("button", { type: "button", class: "pri", disabled: !laBinhLuan, title: laBinhLuan ? "Giao cho vài nick khác vào thích / trả lời đúng bình luận này" : "Chỉ dùng được cho dòng loại bình luận", "data-testid": "wh-giao-bl" }, "Thích / trả lời bình luận này");
      const giaoBai = h("button", { type: "button", title: "Giao cho nhiều nick cùng bình luận vào bài này, mỗi nick một góc nhìn", "data-testid": "wh-giao-bai" }, "Nhiều nick cùng vào bài");
      const baoLoi = (s: string): void => { err.textContent = s; err.hidden = false; };
      /** Cấu hình hội đồng HIỆN TẠI của Boss sở hữu việc này; không đọc được → null (để máy chủ tự quyết khi gửi). */
      const docCauHinh = async (): Promise<CauHinhHoiDongKiem | null> => {
        try { return await api<CauHinhHoiDongKiem>("GET", `/api/hoi-dong${isSuper() && w.ownerId ? buildQuery({ ownerId: w.ownerId }) : ""}`); } catch { return null; }
      };
      // kiem: phép kiểm CHUNG với máy chủ (src/shared/hoi-dong-kiem.ts). Điều kiện máy chủ chắc chắn trả 400 thì báo ngay, KHÔNG gửi:
      // Chromium ghi mọi 4xx vào console, người dùng thấy "lỗi" trong khi trang đã biết trước kết quả (hạt khỉ 28/30/34).
      const chay = (btn: HTMLButtonElement, path: string, body: Record<string, unknown>, kiem: (c: CauHinhHoiDongKiem) => string | null, truoc?: () => string | null) => void busy(btn, "Đang giao…", async () => {
        err.hidden = true;
        try {
          const loiTruoc = truoc?.() ?? null;
          if (loiTruoc) { baoLoi(loiTruoc); return; }
          const cfg = await docCauHinh();
          const loiCfg = cfg ? kiem(cfg) : null;
          if (loiCfg) { baoLoi(loiCfg); return; }
          const r = await api<{ jobIds: string[]; nicks: string[] }>("POST", path, body);
          toast(`Đã giao ${r.nicks.length} nick (${r.jobIds.length} lệnh)`);
          done();
        } catch (e) { baoLoi(errMsg(e)); }
      });
      giaoBL.addEventListener("click", () => chay(giaoBL, "/api/hoi-dong/binh-luan", { url: w.url, chuBinhLuan: chu.value.trim() }, loiThieuKichBanThichTraLoi, () => loiDiaChiHttp(w.url) ?? (chu.value.trim() ? null : LOI_THIEU_CHU)));
      // Đặc tả đổi 12/10/2026: góc nhìn trống / quá nhiều / địa chỉ sai → báo ngay đúng câu của máy chủ, KHÔNG gửi (tránh 400 vô nghĩa).
      giaoBai.addEventListener("click", () => {
        const diaChi = w.parentUrl ?? w.url;
        const gocNhin = chuanHoaGocNhin(goc.value.split("\n"));
        chay(giaoBai, "/api/hoi-dong/bai", { url: diaChi, gocNhin }, loiThieuKichBanBinhLuanBai, () => loiDiaChiHttp(diaChi) ?? loiGocNhinBai(gocNhin));
      });
      const wrap = h("div", { class: "modal-wrap", role: "dialog", "aria-modal": "true", "data-testid": "wh-box" },
        h("div", { class: "modal" },
          h("h3", {}, "Giao hội đồng nhiều nick"),
          h("p", { class: "small wrap" }, shortUrl(w.url, 60)),
          h("p", { class: "small muted" }, "Số nick, giãn cách và hạn mức lấy theo Cài đặt → Hội đồng nhiều nick."),
          h("label", { class: "f" }, h("span", {}, "Đoạn chữ nhận ra bình luận này"), chu),
          h("label", { class: "f" }, h("span", {}, "Góc nhìn cho từng nick (mỗi dòng một góc nhìn)"), goc),
          err,
          h("div", { class: "row end" },
            h("button", { type: "button", title: "Đóng hộp này, không giao gì cả", onclick: done, "data-testid": "wh-close" }, "Đóng"),
            giaoBL, giaoBai)));
      document.body.appendChild(wrap);
      wrap.addEventListener("keydown", (e) => { if (e.key === "Escape") done(); });
    });
  }

  applyBtn.addEventListener("click", () => {
    history.replaceState(null, "", `#/works${buildQuery({ botId: botSel.value, kind: kindSel.value, bundleName: bundle.value.trim(), q: q.value.trim(), from: from.value, to: to.value })}`);
    void busy(applyBtn, "Đang lọc…", () => loadPage(true));
  });
  more.addEventListener("click", () => void busy(more, "Đang tải…", () => loadPage(false)));
  onlyDup.addEventListener("change", () => {
    for (const tr of tbody.querySelectorAll<HTMLTableRowElement>('[data-testid="w-row"]')) tr.hidden = onlyDup.checked && tr.dataset.dup !== "true";
  });
  await loadPage(true);
  return () => {};
}

/** Khối "Đã làm ở đâu" trong chi tiết lần chạy (rỗng thì không hiện gì). */
export async function renderRunWorks(box: HTMLElement, runKey: string): Promise<void> {
  if (!store.user) return;
  let items: Work[] = [];
  try { items = (await api<Paged>("GET", `/api/works${buildQuery({ runKey, limit: 100 })}`)).items; } catch { return; }
  if (!items.length) return;
  box.append(h("div", { class: "card", "data-testid": "run-works" },
    h("h3", {}, `Đã làm ở đâu (${items.length})`),
    h("div", { class: "tbl" }, h("table", {}, h("thead", {}, h("tr", {}, h("th", {}, "Lúc"), h("th", {}, "Bước"), h("th", {}, "Nơi làm"), h("th", { title: "Bấm để mở lại trang đã làm" }, "Địa chỉ"))),
      h("tbody", {}, ...items.map((w) => h("tr", { "data-testid": "run-work-row" },
        h("td", { class: "nowrap small" }, fmtTime(w.at)),
        h("td", { class: "small wrap" }, tenBuoc(w.bundleName, w.stepId)),
        h("td", { class: "wrap small" }, noiLam(w) ?? w.title ?? h("span", { class: "muted" }, "—")),
        h("td", { class: "wrap" }, h("a", { href: w.url, target: "_blank", rel: "noopener noreferrer", title: `Mở lại: ${w.url}`, "data-testid": "run-work-link" }, shortUrl(w.url)),
          w.truocDo ? [" ", h("span", { class: "tag warn", title: `Địa chỉ này đã được làm ${w.truocDo} lần trước đó` }, `làm lần ${w.truocDo + 1}`)] : null))))))));
}
